import { all, call, fork, put, select, take, takeEvery } from 'typed-redux-saga/macro';
import { captureException } from '@sentry/react';

import { resetState as authResetState, setUserId } from 'features/Auth/slice';

import { setId, setSubscription, resetState, setUsername, setCancellationStep } from './slice';
import callApi, { METHODS, PATHS } from 'api';

import type {
  UserSubscriptionCurrency,
  UserSubscriptionStatus,
  UserSubscriptionTier,
} from 'features/User/types';
import { getEmail, getSubscription } from 'features/User/selectors';
import { cancelPlan, updateLanguagePreference, updateStudyInNativeLanguage } from 'features/User/actions';
import { setPreferredLanguageCode, setStudyInNativeLanguage } from './slice';
import { getStudyInNativeLanguage } from './selectors';

import { watchProgressiveWebApp } from './ProgressiveWebApp/sagas';
import { watchBrandReview } from './BrandReview/sagas';

interface UpdatePreferencesParams {
  preferred_language_code: string;
}

function* updatePreferences(params: UpdatePreferencesParams) {
  try {
    const { data } = yield* call(
      callApi<UpdatePreferencesParams, { success: boolean }>,
      {
        method: METHODS.PATCH,
        mainPath: PATHS.USER_PREFERENCES,
        data: params,
        authorized: true,
      }
    );

    return data;
  } catch (error) {
    yield* call(captureException, error, {
      contexts: {
        apiCall: {
          endpoint: PATHS.USER_PREFERENCES,
          method: METHODS.PATCH,
          params,
        },
      },
      tags: {
        api: 'user-preferences',
        method: 'PATCH',
      },
    });
    throw new Error('Failed to update user preferences');
  }
}

function* fetchSubscriptionInfo() {
  try {
    const { data } = yield* call(
      callApi<
        object,
        {
          id: string;
          tier: UserSubscriptionTier;
          subscription_status: UserSubscriptionStatus;
          payment_frequency_months: number;
          payment_frequency_days: number | null;
          next_payment_date: string;
          payment_amount: number;
          currency: UserSubscriptionCurrency;
        }
      >,
      {
        method: METHODS.GET,
        mainPath: PATHS.SUBSCRIPTION,
        authorized: true,
      }
    );

    return data;
  } catch (error) {
    yield* call(captureException, error);
    throw new Error('Failed to fetch subscription data');
  }
}

function* getSubscriptionStatus() {
  const {
    tier,
    subscription_status,
    payment_amount,
    next_payment_date,
    payment_frequency_months,
    payment_frequency_days,
    currency,
  } = yield* call(fetchSubscriptionInfo);

  yield* put(
    setSubscription({
      tier,
      status: subscription_status,
      paymentAmount: payment_amount,
      nextPaymentDate: next_payment_date,
      paymentFrequencyMonths: payment_frequency_months,
      paymentFrequencyDays: payment_frequency_days ?? null,
      currency,
    })
  );
}

function* sendCancellationFeedback(feedback: string) {
  try {
    yield* fork(callApi<{ feedback: string }>, {
      method: METHODS.POST,
      mainPath: PATHS.USER_FEEDBACK,
      data: { feedback, type: 'cancel_reason' },
      authorized: true,
    });
  } catch (error) {
    yield* call(captureException, error);
  }
}

function* updateLanguagePreferenceWorker({ payload }: ReturnType<typeof updateLanguagePreference>) {
  try {
    yield* call(updatePreferences, { preferred_language_code: payload.languageCode });
    yield* put(setPreferredLanguageCode(payload.languageCode));
  } catch (error) {
    yield* call(captureException, error, {
      contexts: {
        updateLanguagePreference: {
          languageCode: payload.languageCode,
          action: 'updateLanguagePreference',
        },
      },
      tags: {
        feature: 'user-preferences',
        action: 'update-language',
      },
    });
  }
}

function* updateStudyInNativeLanguageWorker({ payload }: ReturnType<typeof updateStudyInNativeLanguage>) {
  const previousValue = yield* select(getStudyInNativeLanguage);
  
  // Optimistically update UI for better UX
  yield* put(setStudyInNativeLanguage(payload));
  
  try {
    yield* call(callApi, {
      method: METHODS.PATCH,
      mainPath: PATHS.USER_PREFERENCES,
      data: { study_in_native_language: payload },
      authorized: true,
    });
  } catch (error) {
    // Revert to previous state if API call fails
    yield* put(setStudyInNativeLanguage(previousValue));
    
    yield* call(captureException, error, {
      contexts: {
        updateStudyInNativeLanguage: {
          value: payload,
          action: 'updateStudyInNativeLanguage',
        },
      },
      tags: {
        feature: 'user-preferences',
        action: 'update-study-in-native-language',
      },
    });
  }
}

function* cancelUserSubscriptionWorker() {
  while (true) {
    const {
      payload: { feedback },
    } = yield* take(cancelPlan);

    yield* put(setCancellationStep('loading'));

    yield* fork(sendCancellationFeedback, feedback);

    try {
      yield* call(callApi, {
        method: METHODS.POST,
        mainPath: PATHS.CANCEL_SUBSCRIPTION,
        authorized: true,
      });

      yield* put(setCancellationStep('success'));

      yield* call(getSubscriptionStatus);

      const subscription = yield* select(getSubscription);
      if (subscription) yield* put(setSubscription({ ...subscription, status: 'non_renewing' }));
    } catch (error) {
      yield* put(setCancellationStep('error'));
      yield* call(captureException, error);
    }
  }
}

export function* watchUser() {
  yield* all([
    fork(watchProgressiveWebApp),
    fork(watchBrandReview),
    fork(cancelUserSubscriptionWorker),
    takeEvery(updateLanguagePreference, updateLanguagePreferenceWorker),
    takeEvery(updateStudyInNativeLanguage, updateStudyInNativeLanguageWorker),
    takeEvery(setUserId, function* ({ payload }) {
      yield* put(setId(payload));
      yield* fork(getSubscriptionStatus);
      yield* fork(function* () {
        const email = yield* select(getEmail);
        yield* put(setUsername(email.split('@')[0]));
      });
    }),
    yield* takeEvery(authResetState, function* () {
      yield* put(resetState());
    }),
  ]);
}
